import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useClubSettings } from '../context/ClubSettingsContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Heart,
  MessageCircle,
  Share2,
  ArrowLeft,
  Loader2,
  Send,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  Flag,
  Ban
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import ReportDialog from '../components/ReportDialog';
import BlockUserDialog from '../components/BlockUserDialog';
import ShareButtons from '../components/ShareButtons';
import LinkPreviewCard from '../components/LinkPreviewCard';
import { TagList } from '../components/TagBadge';
import NestedComments from '../components/NestedComments';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { setPageTitle } from '../hooks/usePageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { voteSettings } = useClubSettings();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [commenting, setCommenting] = useState(false);
  const [liked, setLiked] = useState(false);
  const [userVote, setUserVote] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  // Update page title when post loads
  useEffect(() => {
    if (post?.title) {
      setPageTitle(post.title);
    }
  }, [post]);

  const fetchPost = async () => {
    try {
      const [postRes, commentsRes] = await Promise.all([
        axios.get(`${API}/posts/${postId}`),
        axios.get(`${API}/posts/${postId}/comments/tree`)
      ]);
      setPost(postRes.data);
      setComments(commentsRes.data.comments || []);
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Post not found');
      navigate('/forums');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      if (liked) {
        await axios.delete(`${API}/posts/${postId}/like`);
        setLiked(false);
        setPost(prev => ({ ...prev, likes_count: prev.likes_count - 1 }));
      } else {
        await axios.post(`${API}/posts/${postId}/like`);
        setLiked(true);
        setPost(prev => ({ ...prev, likes_count: prev.likes_count + 1 }));
      }
    } catch (error) {
      // Handle already liked/unliked
    }
  };

  const handleVote = async (voteType) => {
    if (!user) {
      navigate('/login');
      return;
    }
    try {
      const response = await axios.post(`${API}/posts/${postId}/vote`, { vote_type: voteType });
      const newVote = response.data.vote;
      
      const oldVote = userVote;
      let scoreChange = 0;
      
      if (oldVote === null) {
        scoreChange = newVote === 'up' ? 1 : newVote === 'down' ? -1 : 0;
      } else if (newVote === null) {
        scoreChange = oldVote === 'up' ? -1 : 1;
      } else {
        scoreChange = newVote === 'up' ? 2 : -2;
      }
      
      setUserVote(newVote);
      setPost(prev => ({ ...prev, score: (prev.score || 0) + scoreChange }));
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }
    if (!newComment.trim()) return;

    setCommenting(true);
    try {
      const response = await axios.post(`${API}/posts/${postId}/comments`, {
        content: newComment.trim()
      });
      setComments([...comments, response.data]);
      setPost(prev => ({ ...prev, comments_count: prev.comments_count + 1 }));
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setCommenting(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'general': 'bg-muted text-muted-foreground',
      'care-tips': 'bg-primary/10 text-primary',
      'identification': 'bg-info/10 text-info',
      'showcase': 'bg-secondary/10 text-secondary',
      'question': 'bg-warning/10 text-warning',
      'news': 'bg-accent text-accent-foreground'
    };
    return colors[category] || colors.general;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-6"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {/* Post */}
          <Card className="mb-8" data-testid="post-detail">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Avatar 
                    className="w-12 h-12 cursor-pointer"
                    onClick={() => navigate(`/profile/${post.author_username}`)}
                  >
                    <AvatarImage src={post.author_avatar} />
                    <AvatarFallback>{post.author_username?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p 
                      className="font-medium hover:underline cursor-pointer"
                      onClick={() => navigate(`/profile/${post.author_username}`)}
                    >
                      @{post.author_username}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getCategoryColor(post.category)}>
                    {post.category}
                  </Badge>
                  {user && user.id !== post.author_id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="post-more-menu">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <ReportDialog
                          contentType="post"
                          contentId={post.id}
                          trigger={
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid="report-post-btn">
                              <Flag className="w-4 h-4 mr-2" />
                              Report Post
                            </DropdownMenuItem>
                          }
                        />
                        <BlockUserDialog
                          userId={post.author_id}
                          username={post.author_username}
                          trigger={
                            <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid="block-post-author-btn">
                              <Ban className="w-4 h-4 mr-2" />
                              Block @{post.author_username}
                            </DropdownMenuItem>
                          }
                          onBlocked={() => {
                            toast.success(`@${post.author_username} has been blocked`);
                            navigate('/feed');
                          }}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mb-4">
                  <TagList 
                    tags={post.tags} 
                    size="sm"
                  />
                </div>
              )}

              <h1 className="text-2xl font-heading font-medium mb-4">{post.title}</h1>
              <p className="text-muted-foreground whitespace-pre-wrap mb-6">{post.content}</p>
              
              {/* Link Preview */}
              {post.link_preview && (
                <LinkPreviewCard preview={post.link_preview} className="mb-6" />
              )}

              {post.image_url && (
                <div className="rounded-xl overflow-hidden mb-6">
                  <img 
                    src={post.image_url} 
                    alt={post.title}
                    className="w-full"
                  />
                </div>
              )}

              <div className="flex items-center gap-6 pt-4 border-t">
                <div className="flex items-center gap-1">
                  <button
                    className={`p-1.5 rounded transition-colors ${
                      userVote === 'up' 
                        ? 'text-primary bg-primary/10' 
                        : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                    }`}
                    onClick={() => handleVote('up')}
                    data-testid="post-upvote-btn"
                  >
                    <ChevronUp className="w-5 h-5" />
                  </button>
                  <span className={`text-lg font-medium min-w-[2.5rem] text-center ${
                    (post.score || 0) > 0 ? 'text-primary' : 
                    (post.score || 0) < 0 ? 'text-destructive' : 'text-muted-foreground'
                  }`}>
                    {post.score || 0}
                  </span>
                  <button
                    className={`p-1.5 rounded transition-colors ${
                      userVote === 'down' 
                        ? 'text-destructive bg-destructive/10' 
                        : 'text-muted-foreground hover:text-destructive hover:bg-destructive/5'
                    }`}
                    onClick={() => handleVote('down')}
                    data-testid="post-downvote-btn"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                </div>
                <button 
                  className={`flex items-center gap-2 transition-colors ${
                    liked ? 'text-secondary' : 'text-muted-foreground hover:text-secondary'
                  }`}
                  onClick={handleLike}
                  data-testid="like-post-btn"
                >
                  <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
                  <span>{post.likes_count} likes</span>
                </button>
                <span className="flex items-center gap-2 text-muted-foreground">
                  <MessageCircle className="w-5 h-5" />
                  <span>{post.comments_count} comments</span>
                </span>
                <div className="ml-auto">
                  <ShareButtons
                    url={`/post/${post.id}`}
                    title={post.title}
                    description={post.content?.substring(0, 150)}
                    source="post_detail"
                    showRepost={!!user}
                    showCustomUrl={true}
                    onRepost={async (comment) => {
                      const response = await fetch(`${API}/shares/repost`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          original_post_id: post.id,
                          comment
                        })
                      });
                      if (!response.ok) throw new Error('Failed to repost');
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <div className="space-y-6">
            <h2 className="text-xl font-heading font-medium">Comments ({comments.length})</h2>

            {/* Comment Form */}
            {user ? (
              <Card>
                <CardContent className="p-4">
                  <form onSubmit={handleComment} className="flex gap-3">
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarImage src={user.avatar_url} />
                      <AvatarFallback>{user.display_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-3">
                      <Textarea
                        placeholder="Write a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={2}
                        data-testid="comment-input"
                      />
                      <div className="flex justify-end">
                        <Button 
                          type="submit" 
                          className="btn-primary"
                          disabled={commenting || !newComment.trim()}
                          data-testid="submit-comment-btn"
                        >
                          {commenting ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Comment
                        </Button>
                      </div>
                    </div>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-muted-foreground mb-2">Sign in to leave a comment</p>
                  <Button variant="outline" onClick={() => navigate('/login')}>
                    Sign In
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Comments List - Reddit-style Nested Comments */}
            <Card>
              <CardContent className="p-4">
                <NestedComments
                  postId={postId}
                  comments={comments}
                  maxDepth={4}
                  currentUserId={user?.id}
                  onCommentAdded={fetchPost}
                  voteTheme={voteSettings.vote_theme}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;
